import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Alert,
  Card,
  Spinner,
} from "react-bootstrap";
import { FaEye, FaTimes } from "react-icons/fa";
import api, { getFlaggedSystemQuestions, toggleFlaggedStatus } from "../api";
import MathJaxRenderer from "./MathJaxRenderer";

const FlaggedQuestions = () => {
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showQuestion, setShowQuestion] = useState(false);
  const [unflaggingIds, setUnflaggingIds] = useState({});

  // Fetch flagged questions on component mount
  useEffect(() => {
    fetchFlaggedQuestions();
  }, []);

  // Function to fetch flagged questions from API
  const fetchFlaggedQuestions = async () => {
    setLoading(true);
    try {
      const systemQuestions = await getFlaggedSystemQuestions();

      // Format the system questions
      const formattedQuestions = systemQuestions.map((q) => ({
        id: `system-${q.system_id}`,
        system_id: q.system_id,
        title: q.title || `System Question ${q.system_id}`,
        subject: q.subject || "-",
        level: q.level || "-",
        created_at: q.created_at || new Date().toISOString(),
        source: "system",
        flagged: true,
        question_body:
          q.question_body || "<p>Question content will load when viewed</p>",
        markscheme_body:
          q.markscheme_body || "<p>Markscheme will load when viewed</p>",
      }));

      setFlaggedQuestions(formattedQuestions);
      setError(null);
    } catch (err) {
      console.error("Error fetching flagged questions:", err);
      setError("Failed to load flagged questions. Please try again.");
      setFlaggedQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle unflagging a question
  const handleUnflag = async (questionId) => {
    // Update loading state for this specific question
    setUnflaggingIds((prev) => ({ ...prev, [questionId]: true }));

    try {
      // Call our direct API method to toggle the flag (unflag in this case)
      await toggleFlaggedStatus("system", questionId);

      // Remove the question from the list on success
      setFlaggedQuestions((prev) =>
        prev.filter((q) => q.system_id !== questionId)
      );
    } catch (error) {
      console.error(`Error unflagging question ${questionId}:`, error);
      setError("Failed to unflag question. Please try again.");
    } finally {
      // Clear loading state
      setUnflaggingIds((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  // Function to view a question
  const handleViewQuestion = (question) => {
    setSelectedQuestion(question);
    setShowQuestion(true);
  };

  // Close question viewer
  const handleCloseQuestion = () => {
    setShowQuestion(false);
    setSelectedQuestion(null);
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p>Loading flagged questions...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2>Flagged Questions</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {flaggedQuestions.length === 0 ? (
        <Alert variant="info">
          You don't have any flagged questions. Flag questions in the Rumble
          Search to add them here.
        </Alert>
      ) : (
        <>
          <p>These are questions you've flagged for easy reference.</p>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Subject</th>
                <th>Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flaggedQuestions.map((question) => (
                <tr key={question.id}>
                  <td>{question.title}</td>
                  <td>{question.subject}</td>
                  <td>{question.level}</td>
                  <td style={{ width: "180px" }}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleViewQuestion(question)}
                    >
                      <FaEye /> View
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleUnflag(question.system_id)}
                      disabled={unflaggingIds[question.system_id]}
                    >
                      {unflaggingIds[question.system_id] ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <>
                          <FaTimes /> Unflag
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {showQuestion && selectedQuestion && (
        <div className="fixed-overlay">
          <Card className="question-viewer">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>{selectedQuestion.title}</h5>
              <Button variant="outline-secondary" onClick={handleCloseQuestion}>
                Close
              </Button>
            </Card.Header>
            <Card.Body>
              <div className="question-content">
                <h6>Question</h6>
                <MathJaxRenderer html={selectedQuestion.question_body} />

                <h6 className="mt-4">Markscheme</h6>
                <MathJaxRenderer html={selectedQuestion.markscheme_body} />
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      <style jsx="true">{`
        .fixed-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .question-viewer {
          width: 80%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .question-content {
          padding: 10px;
        }
      `}</style>
    </Container>
  );
};

export default FlaggedQuestions;
